import {
  StoreV2,
  mapRecord,
  arrayToMap,
  sortBy,
  filterRecord,
  GridColumnV2,
} from '@schenck/rotec-components';
import { skipWhile, map } from 'rxjs/operators';
import { SettingsApiBase, SettingsBase } from 'src/api';
import { Observable } from 'rxjs';

/**
 * For each column that can be displayed, its name (which will be translated) and its key.
 */
export interface ColumnTitle<Key extends string | number | symbol> {
  name: string;
  key: Key;
}

/**
 * Convenience base class for a generic grid settings service.
 */
export abstract class SettingsServiceBase<
  ColumnKeys extends string,
  Settings extends SettingsBase<ColumnKeys>,
  SettingsApi extends SettingsApiBase<ColumnKeys, Settings>
> extends StoreV2<Settings & { initialized: boolean }> {
  /**
   * Same as state$, but waits with emitting values until settings initialized.
   */
  settings$ = this.state$.pipe(skipWhile((settings) => !settings.initialized));

  constructor(
    private settingsApi: SettingsApi,
    public columnTitles: ColumnTitle<ColumnKeys>[],
    settingsDefaults: Settings
  ) {
    super(
      {
        ...settingsDefaults,
        initialized: false,
      },
      'SettingsService'
    );
    this.loadSettings();
  }

  /**
   * Updates settings and persists that in the backend.
   */
  updateColumnSettings(columns: GridColumnV2[]) {
    this.updateSettings(<Partial<Settings>>{
      columnOrder: columns.map((col) => <ColumnKeys>col.key),
      columnsWidths: mapRecord(arrayToMap(columns, 'key'), (col) => col.width),
      displayedColumns: columns.filter((col) => !col.invisible).map((col) => col.key),
    });
  }

  /**
   * Creates an observable of a sorted list of columns from a record of column definitions and column settings.
   */
  createColumns$(
    columnDefs: Partial<Record<ColumnKeys, GridColumnV2>>
  ): Observable<GridColumnV2[]> {
    return this.state$.pipe(
      map((settings) => {
        const columns = Object.keys(columnDefs).map(
          (colKey: any) =>
            <GridColumnV2>{
              ...columnDefs[colKey],
              invisible: !settings.displayedColumns.includes(colKey),
              width: settings.columnsWidths[colKey] || columnDefs[colKey].width,
            }
        );

        const sortedColumns = sortBy(columns, (col) =>
          settings.columnOrder.indexOf(<ColumnKeys>col.key)
        );
        return sortedColumns;
      })
    );
  }

  /**
   * Updates settings and persists that in the backend.
   * If you call this, you likely need to make a cast via `<Partial<Settings>>`
   * because Typescript cannot infer the generic partial type.
   */
  updateSettings(settings: Partial<Settings>) {
    this.setState('updated settings', (state) => ({
      ...this.sanitizeSettings({
        ...state,
        ...settings,
      }),
      initialized: true,
    }));
    this.settingsApi.setSettings(this.state);
  }

  private loadSettings() {
    this.settingsApi
      .loadSettings$()
      .subscribe((settings) =>
        this.setState('loaded settings', { ...this.sanitizeSettings(settings), initialized: true })
      );
  }

  private sanitizeSettings(settings: Settings): Settings {
    // filter out all settings which key is not known.
    return <Settings>{
      columnOrder: (settings.columnOrder || []).filter((key) =>
        this.columnTitles.some((col) => col.key === key)
      ),
      displayedColumns: (settings.displayedColumns || []).filter((key) =>
        this.columnTitles.some((col) => col.key === key)
      ),
      pageSize: settings.pageSize || this.state.pageSize || 15,
      columnsWidths: filterRecord((settings.columnsWidths || []) as any, (_m, key) =>
        this.columnTitles.some((col) => col.key === key)
      ),
    };
  }
}
